// Boxes are rectangles in page units (a PDF page's points at scale 1, y down), stored by center, size and a
// clockwise angle in degrees, so a tilted receipt can be boxed tightly and straightened when it is cropped.
export const MIN_BOX_SIZE = 12

const toRadians = (degrees) => (degrees * Math.PI) / 180

const rotate = ({ x, y }, degrees) => {
  const a = toRadians(degrees)
  return { x: x * Math.cos(a) - y * Math.sin(a), y: x * Math.sin(a) + y * Math.cos(a) }
}

export const normalizeAngle = (degrees) => ((Math.round(degrees) % 360) + 360) % 360

export const newBox = (page, { width, height }, index) => {
  const w = width * 0.3
  const h = height * 0.3
  const offset = (index % 5) * width * 0.04
  return { id: `${page}-${Date.now()}-${index}`, cx: width / 2 + offset, cy: height / 2 + offset, w, h, angle: 0 }
}

export const moveBox = (box, dx, dy) => ({ ...box, cx: box.cx + dx, cy: box.cy + dy })

export const rotateBox = (box, degrees) => ({ ...box, angle: normalizeAngle(box.angle + degrees) })

// Dragging a corner keeps the opposite corner where it is. corner is [-1|1, -1|1] in the box's own frame.
export const resizeFromCorner = (box, corner, point) => {
  const [sx, sy] = corner
  const local = rotate({ x: point.x - box.cx, y: point.y - box.cy }, -box.angle)
  const fixed = { x: (-sx * box.w) / 2, y: (-sy * box.h) / 2 }
  const w = Math.max(MIN_BOX_SIZE, sx * (local.x - fixed.x))
  const h = Math.max(MIN_BOX_SIZE, sy * (local.y - fixed.y))
  const center = rotate({ x: fixed.x + (sx * w) / 2, y: fixed.y + (sy * h) / 2 }, box.angle)
  return { ...box, cx: box.cx + center.x, cy: box.cy + center.y, w, h }
}

// The rotate handle sits above the box's top edge, so pointing straight up means 0°. Snaps to square within 3°.
export const angleTowards = (box, point) => {
  const degrees = (Math.atan2(point.y - box.cy, point.x - box.cx) * 180) / Math.PI + 90
  const snapped = Math.round(degrees / 90) * 90
  return normalizeAngle(Math.abs(degrees - snapped) <= 3 ? snapped : degrees)
}

export const boundingSize = ({ w, h, angle }) => {
  const a = toRadians(angle)
  return {
    width: Math.abs(w * Math.cos(a)) + Math.abs(h * Math.sin(a)),
    height: Math.abs(w * Math.sin(a)) + Math.abs(h * Math.cos(a))
  }
}

// Top to bottom, then left to right within a row; a box joins a row when its center is above the first box's
// bottom half, so slightly staggered receipts still read as one row.
export const readingOrder = (boxes) => {
  const rows = []
  ;[...boxes]
    .sort((a, b) => a.cy - b.cy)
    .forEach((box) => {
      const row = rows[rows.length - 1]
      if (row && box.cy - row[0].cy < boundingSize(row[0]).height / 2) row.push(box)
      else rows.push([box])
    })
  return rows.flatMap((row) => row.sort((a, b) => a.cx - b.cx))
}
